import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import formatDate from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

import {
  formatNumber,
  dateFilterParams,
  formatLongPrice,
  headerInfoTooltipDef,
  getValueBgClass,
  getPureNumber,
  dateValueGetter,
  transformPercentageValue,
} from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const EPSFilter = [
  {
    label: '$0.00 to $0.50',
    rules: (value: number) => {
      return value >= 0.0 && value <= 0.5;
    },
  },
  {
    label: '$0.51 to $1.00',
    rules: (value: number) => {
      return value >= 0.51 && value <= 1.0;
    },
  },
  {
    label: '$1.01 to $3.00',
    rules: (value: number) => {
      return value >= 1.01 && value <= 3.0;
    },
  },
  {
    label: '$3.00+',
    rules: (value: number) => {
      return value >= 3.0;
    },
  },
  {
    label: '$0.00 to -$0.50',
    rules: (value: number) => {
      return value <= 0.0 && value >= -0.5;
    },
  },
  {
    label: '-$0.51 to -$1.00',
    rules: (value: number) => {
      return value <= -0.51 && value >= -1.0;
    },
  },
  {
    label: '-$1.01 to -$3.00',
    rules: (value: number) => {
      return value <= -1.01 && value >= -3.0;
    },
  },
  {
    label: 'Less Than -$3.00',
    rules: (value: number) => {
      return value <= -3.0;
    },
  },
];

const REVFilter = [
  {
    label: '$0 to $100M',
    rules: (value: number) => {
      return value >= 0 && value <= 100000000;
    },
  },
  {
    label: '$100M to $250M',
    rules: (value: number) => {
      return value >= 100000000 && value <= 250000000;
    },
  },
  {
    label: '$250M to $500M',
    rules: (value: number) => {
      return value >= 250000000 && value <= 500000000;
    },
  },
  {
    label: '$500M to $2B',
    rules: (value: number) => {
      return value >= 250000000 && value <= 2000000000;
    },
  },
  {
    label: '$2B+',
    rules: (value: number) => {
      return value >= 2000000000;
    },
  },
];

const percentageSurpriseFilter = [
  {
    label: '< -5%',
    rules: (value: number) => {
      return value <= -5;
    },
  },
  {
    label: '-5% - 0%',
    rules: (value: number) => {
      return value >= -5 && value <= 0;
    },
  },
  {
    label: '0% - 5%',
    rules: (value: number) => {
      return value >= 0 && value <= 5;
    },
  },
  {
    label: '5% - 10%',
    rules: (value: number) => {
      return value >= 5 && value <= 10;
    },
  },
  {
    label: '> 10%',
    rules: (value: number) => {
      return value >= 10;
    },
  },
];

export default [
  // {
  //   cellClass: 'ticker-cell',
  //   field: 'time',
  //   filter: 'agSetColumnFilter',
  //   filterParams: {
  //     ...defaultFilterParams,
  //     buttons: ['clear'],
  //   },
  //   valueGetter: ({ data }) => {
  //     if (!data.time) return '';

  //     return formatDate(parse(data.time, 'HH:mm:ss', new Date()), 'a');
  //   },
  //   initialWidth: 50,
  // },
  {
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    cellRendererParams: {
      tickerLogoVisible: false,
    },
    field: 'ticker',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 100,
  },
  {
    cellClass: 'ticker-cell',
    cellRendererFramework: ({ data }: any) => {
      return (
        <div className="company-name-cell">
          <span>{data.name}</span>
          {data.logo && <img alt="" className="bz-ag-table__company-image" loading="lazy" src={data.logo}></img>}
        </div>
      );
    },
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company Name',
    initialWidth: 160,
    ...headerInfoTooltipDef('Full name of company associated to ticker'),
  },
  // {
  //   cellClass: 'ticker-cell',
  //   field: 'period',
  //   filter: 'agSetColumnFilter',
  //   filterParams: {
  //     ...defaultFilterParams,
  //     buttons: ['clear'],
  //     defaultToNothingSelected: true,
  //   },
  //   headerName: 'Quarter',
  //   initialWidth: 60,
  // },
  {
    cellClass: 'ticker-cell',
    field: 'eps_prior',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: EPSFilter,
    },
    headerName: 'Prior EPS',
    initialWidth: 100,
    valueFormatter: ({ value }) => formatLongPrice(value),
    valueGetter: ({ data }) => getPureNumber(data.eps_prior),
  },
  {
    cellClass: 'ticker-cell',
    field: 'eps_est',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: EPSFilter,
    },
    headerName: 'Est EPS',
    initialWidth: 90,
    valueFormatter: ({ value }) => formatLongPrice(value),
    valueGetter: ({ data }) => getPureNumber(data.eps_est),
  },
  {
    cellClass: 'ticker-cell',
    field: 'eps',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: EPSFilter,
    },
    headerName: 'Actual EPS',
    initialWidth: 110,
    valueFormatter: ({ value }) => formatLongPrice(value),
    valueGetter: ({ data }) => getPureNumber(data.eps),
  },
  {
    field: 'eps_surprise_percent',
    filter: 'priceFilter',
    headerName: 'EPS Surprise',
    ...headerInfoTooltipDef('Earnings Per Share (EPS) Surprise.'),
    cellClass: ({ value }) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: percentageSurpriseFilter,
    },
    initialWidth: 100,
    valueFormatter: ({ value }) => formatNumber(value, '%'),
    valueGetter: ({ data }) => transformPercentageValue(data.eps_surprise_percent),
  },
  {
    cellClass: 'ticker-cell',
    field: 'revenue_prior',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: REVFilter,
    },
    headerName: 'Prior Rev',
    initialWidth: 100,
    valueFormatter: ({ value }) => formatLongPrice(value),
    valueGetter: ({ data }) => getPureNumber(data.revenue_prior),
  },

  {
    cellClass: 'ticker-cell',
    field: 'revenue_est',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: REVFilter,
    },
    headerName: 'Est Rev',
    initialWidth: 90,
    sortable: true,
    valueFormatter: ({ value }) => formatLongPrice(value),
    valueGetter: ({ data }) => getPureNumber(data.revenue_est),
  },
  {
    cellClass: 'ticker-cell',
    field: 'revenue',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: REVFilter,
    },
    headerName: 'Actual Rev',
    initialWidth: 110,
    valueFormatter: ({ value }) => formatLongPrice(value),
    valueGetter: ({ data }) => getPureNumber(data.revenue),
  },

  {
    field: 'revenue_surprise_percent',
    filter: 'priceFilter',
    headerName: 'Rev Surprise',
    ...headerInfoTooltipDef('Revenue Surprise.'),
    cellClass: ({ value }) => getValueBgClass(value),
    cellStyle: { fontWeight: 'bold', textAlign: 'left' },
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: percentageSurpriseFilter,
    },
    initialWidth: 100,
    valueFormatter: ({ value }) => formatNumber(value, '%'),
    valueGetter: ({ data }) => transformPercentageValue(data.revenue_surprise_percent),
  },
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Date',
    initialSort: 'desc' as ColDef['initialSort'],
    initialWidth: 100,
    valueFormatter: ({ value }) => formatDate(parseISO(value), 'MM/dd/yyyy'),
    valueGetter: ({ data }) => dateValueGetter(data.date),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    suppressAutoSize: true,
    suppressMenu: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
